/**
 * Presell page /pages/applique-backpacks (templates/page.afl-seen*.json) — the
 * behaviours the afroyla-seen-* sections need that are NOT the buy box.
 *
 *   1. Reveal hotspots `[data-seen-hotspot]`: one card open at a time,
 *      aria-expanded on the dot, Escape / outside tap closes.
 *   2. Add-to-cart `[data-seen-add]`: posts its own `data-variant-id`, then
 *      fires `theme:cart:refresh` (the bridge in afroyla-theme-events.js turns
 *      that into shopify:cart:lines-update with action 'add', which opens
 *      Horizon's cart drawer).
 *   3. Sticky-bar gate: stamps <html data-afl-seen-past-fold> once the fold has
 *      scrolled above the viewport (afroyla-seen.css parks the bar until then;
 *      afroyla-lp-sticky.js handles the rest).
 *   4. Window scroll lock while .page-wrapper is the scroller.
 *
 * The BUY BOX lives in its own module, assets/afroyla-seen-buy.js (v8, tier
 * cards): design picking, pricing, the receipt and the cart permalink. It
 * makes no /discount/ call — the automatic discount applies in checkout.
 */
(function () {
  'use strict';

  if (window.__aflSeenInit) return;
  window.__aflSeenInit = true;

  function toInt(value) {
    var n = parseInt(value, 10);
    return isNaN(n) ? 0 : n;
  }

  /* ---- 1. hotspots -------------------------------------------------------- */
  var openSpot = null;

  function setSpot(spot, open) {
    var dot = spot.querySelector('[data-seen-hotspot-dot]');
    var card = spot.querySelector('[data-seen-hotspot-card]');
    spot.setAttribute('data-open', open ? 'true' : 'false');
    if (dot) dot.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (card) card.hidden = !open;
  }

  function closeSpot() {
    if (!openSpot) return;
    setSpot(openSpot, false);
    openSpot = null;
  }

  function toggleSpot(spot) {
    var wasOpen = openSpot === spot;
    closeSpot();
    if (wasOpen) return;
    setSpot(spot, true);
    openSpot = spot;
    var frame = spot.closest('[data-seen-reveal]');
    var hint = frame && frame.querySelector('[data-seen-hint]');
    if (hint) hint.setAttribute('data-hidden', 'true');
  }

  function initHotspots() {
    document.querySelectorAll('[data-seen-hotspot]').forEach(function (spot) {
      var card = spot.querySelector('[data-seen-hotspot-card]');
      if (card) card.hidden = true;
      spot.setAttribute('data-open', 'false');
    });
  }

  function setBtn(btn, state, text) {
    btn.setAttribute('data-state', state);
    btn.disabled = state === 'pending';
    btn.setAttribute('aria-busy', state === 'pending' ? 'true' : 'false');
    if (text) btn.textContent = text;
  }

  /* ---- 2. add to cart ------------------------------------------------------ */
  function refreshCart() {
    document.dispatchEvent(new CustomEvent('theme:cart:refresh'));
  }

  function postCart(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items }),
    }).then(function (res) {
      if (!res.ok) throw new Error('HTTP ' + res.status);
      return res.json();
    });
  }

  function addToCart(btn) {
    var state = btn.getAttribute('data-state');
    if (state === 'pending') return;
    if (state === 'added') {
      refreshCart();
      return;
    }

    var items = [{ id: toInt(btn.getAttribute('data-variant-id')), quantity: 1 }].filter(function (item) {
      return item.id > 0;
    });
    if (!items.length) {
      setBtn(btn, 'error', btn.getAttribute('data-label-error'));
      return;
    }

    var idle = btn.getAttribute('data-label-idle') || btn.textContent;
    btn.setAttribute('data-label-idle', idle);
    setBtn(btn, 'pending', btn.getAttribute('data-label-pending') || idle);

    postCart(items)
      .then(function () {
        setBtn(btn, 'added', btn.getAttribute('data-label-added'));
        refreshCart();
      })
      .catch(function () {
        setBtn(btn, 'error', btn.getAttribute('data-label-error'));
        window.setTimeout(function () {
          if (btn.getAttribute('data-state') === 'error') setBtn(btn, 'idle', idle);
        }, 4000);
      });
  }

  /* ---- 3. sticky-bar gate --------------------------------------------------
     afroyla-lp-sticky.js shows the bar whenever [data-afl-grid] (= the buy box)
     is off screen and hides it on the close band. That alone would pin the bar
     over the fold, so stamp <html> with whether the fold has been scrolled PAST;
     afroyla-seen.css parks the bar until then. */
  function initPastFold() {
    var fold = document.querySelector('.afl-seen-fold');
    if (!fold) return;
    var ticking = false;
    var last = null;
    function measure() {
      ticking = false;
      var past = fold.getBoundingClientRect().bottom <= 0;
      if (past === last) return;
      last = past;
      document.documentElement.setAttribute('data-afl-seen-past-fold', past ? 'true' : 'false');
    }
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(measure);
    }
    // A scroll listener, not IntersectionObserver: an anchor jump that skips
    // straight past the fold never changes its intersection state, so an
    // observer would miss it and leave the bar parked.
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    // Desktop Horizon scrolls an inner container (.page-wrapper), not the
    // window: listen there too, or the gate never flips on desktop.
    var wrapper = document.querySelector('.page-wrapper');
    if (wrapper) wrapper.addEventListener('scroll', onScroll, { passive: true });
    measure();
  }

  /* ---- 4. window scroll lock -----------------------------------------------
     When .page-wrapper is the scroller, <html> is overflow:hidden but still
     5000px+ tall and still scrollable by focus() — ticking an add-on
     checkbox focused it and the browser scrolled the WINDOW to a blank area
     below the 100vh wrapper (owner: "màn hình trắng xoá", 03.09 23:39).
     overflow:clip forbids every kind of scrolling, including focus and
     script; only applied while the wrapper really is the scroller. */
  function initWindowScrollLock() {
    var html = document.documentElement;
    var wrapper = document.querySelector('.page-wrapper');
    if (!wrapper) return;
    function wrapperScrolls() {
      return getComputedStyle(wrapper).overflowY !== 'visible' && wrapper.scrollHeight > wrapper.clientHeight + 1;
    }
    function apply() {
      html.style.overflow = wrapperScrolls() ? 'clip' : '';
    }
    // overflow:clip on the root is downgraded to hidden by the viewport, so
    // the window can still be scrolled by focus(). Two guards: pointer
    // interactions inside the buy box never focus (mousedown default off —
    // click/toggle still fires), and any window scroll that happens while the
    // wrapper is the scroller is undone on the next frame.
    function guard() {
      if (window.scrollY && wrapperScrolls()) window.scrollTo(0, 0);
    }
    document.addEventListener(
      'mousedown',
      function (event) {
        if (!event.target.closest) return;
        var control = event.target.closest(
          '#seen-buy label, #seen-buy button, #seen-buy summary, #seen-buy [role="radio"], #seen-buy input'
        );
        if (control && wrapperScrolls()) event.preventDefault();
      },
      true
    );
    document.addEventListener(
      'focusin',
      function () {
        window.requestAnimationFrame(guard);
      },
      true
    );
    window.addEventListener('scroll', guard, { passive: true });
    apply();
    window.addEventListener('resize', apply, { passive: true });
    window.addEventListener('load', apply, { passive: true });
  }

  /* ---- wiring ------------------------------------------------------------- */
  document.addEventListener('click', function (event) {
    if (!event.target.closest) return;

    var dot = event.target.closest('[data-seen-hotspot-dot]');
    if (dot) {
      event.preventDefault();
      var spot = dot.closest('[data-seen-hotspot]');
      if (spot) toggleSpot(spot);
      return;
    }

    var add = event.target.closest('[data-seen-add]');
    if (add) {
      event.preventDefault();
      addToCart(add);
      return;
    }

    if (openSpot && !event.target.closest('[data-seen-hotspot]')) closeSpot();
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape' || !openSpot) return;
    var dot = openSpot.querySelector('[data-seen-hotspot-dot]');
    closeSpot();
    if (dot) dot.focus();
  });

  function boot() {
    initHotspots();
    initPastFold();
    initWindowScrollLock();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
